import { Eye, EyeOff, UserPlus } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { InputGroup } from "@/components/ui/Input";
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { Switch } from "@/components/ui/Switch";
import { toast } from "@/components/ui/Toast";
import { api } from "@/lib/api-client";
import { useTranslation } from "@/hooks/useTranslation";

// ─────────────────────────────────────────────
// CreateUser — Screen 15 (EKJQq)
//
// User creation form page.
// Design: §8.5 Screen 15
// ─────────────────────────────────────────────

interface Role {
  id: string;
  name: string;
}

interface Entity {
  id: string;
  name: string;
  type: string;
}

interface CreateUserForm {
  name: string;
  email: string;
  password: string;
  roleId: string;
  // entityId holds the selected Entity's id; we resolve to name on submit
  entityId: string;
  isActive: boolean;
}

const EMPTY_FORM: CreateUserForm = {
  name: "",
  email: "",
  password: "",
  roleId: "",
  entityId: "",
  isActive: true,
};

export function CreateUser() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [form, setForm] = useState<CreateUserForm>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof CreateUserForm, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [roles, setRoles] = useState<Role[]>([]);
  const [entities, setEntities] = useState<Entity[]>([]);

  useEffect(() => {
    api
      .get<{ data: Role[] }>("/roles")
      .then((res) => setRoles(res.data ?? []))
      .catch(() => {});
    api
      .get<{ data: Entity[] }>("/entities?pageSize=100&status=ACTIVE")
      .then((res) => setEntities(res.data ?? []))
      .catch(() => {});
  }, []);

  function validate(): boolean {
    const next: typeof errors = {};
    if (!form.name.trim()) next.name = t("users.fullNameRequired");
    if (!form.email.trim()) next.email = t("users.emailRequired");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      next.email = t("users.emailInvalid");
    if (!form.password) next.password = t("users.passwordRequired");
    else if (form.password.length < 8) next.password = t("users.passwordMinLength");
    else if (!/[A-Z]/.test(form.password)) next.password = t("users.passwordNeedsUppercase");
    else if (!/[a-z]/.test(form.password)) next.password = t("users.passwordNeedsLowercase");
    else if (!/[0-9]/.test(form.password)) next.password = t("users.passwordNeedsDigit");
    if (!form.roleId) next.roleId = t("users.roleRequired");
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);

    // Resolve entityId → entity name (the backend stores a plain text string)
    const selectedEntity = entities.find((en) => en.id === form.entityId);

    try {
      await api.post("/users", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        roleId: form.roleId || undefined,
        entity: selectedEntity?.name ?? undefined,
        // Account Status switch → user status. POST /users now honors this
        // (createUserSchema accepts `status`; the profile is created with it),
        // and a DISABLED account is blocked at the request layer (S-136).
        status: form.isActive ? "ACTIVE" : "DISABLED",
      });
      toast.success(t("users.userCreated"), `${form.name} ${t("users.invitedSuffix")}`);
      navigate("/users");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      if (message.includes("409") || message.toLowerCase().includes("duplicate")) {
        setErrors((prev) => ({
          ...prev,
          email: t("users.emailInUse"),
        }));
      } else {
        toast.error(t("users.createFailedTitle"), message);
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-caption text-text-muted">
        <button
          type="button"
          className="transition-colors hover:text-text-secondary"
          onClick={() => navigate("/users")}
        >
          {t("users.title")}
        </button>
        <span>/</span>
        <span className="text-text-secondary">{t("users.createNew")}</span>
      </nav>

      {/* Page header */}
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-[24px] font-semibold text-text-primary">
            {t("users.createNew")}
          </h1>
          <p className="text-body text-text-secondary">
            {t("users.createNewSubtitle")}
          </p>
        </div>
      </div>

      {/* Form card */}
      <form
        onSubmit={handleSubmit}
        className="rounded-card border border-border-default bg-card p-6"
      >
        {/* Card header */}
        <div className="mb-6 flex items-center gap-3 border-b border-border-subtle pb-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-card bg-hover">
            <UserPlus className="h-5 w-5 text-text-muted" />
          </div>
          <h2 className="text-[16px] font-medium text-text-primary">
            {t("users.userInformation")}
          </h2>
        </div>

        <div className="flex flex-col gap-5">
          {/* Row 1: Name + Email */}
          <div className="grid grid-cols-2 gap-4">
            <InputGroup
              label={t("settings.fullName")}
              required
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder={t("users.namePlaceholder")}
              errorText={errors.name}
            />
            <InputGroup
              label={t("settings.email")}
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder={t("users.emailPlaceholder")}
              errorText={errors.email}
            />
          </div>

          {/* Row 2: Password */}
          <div className="relative">
            <InputGroup
              label={t("users.initialPassword")}
              required
              type={showPassword ? "text" : "password"}
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              placeholder={t("users.passwordHint")}
              errorText={errors.password}
            />
            <button
              type="button"
              aria-label={showPassword ? t("auth.hide") : t("auth.show")}
              className="absolute right-3 top-[34px] text-text-muted hover:text-text-secondary"
              onClick={() => setShowPassword((v) => !v)}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>

          {/* Row 3: Role + Entity */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-[6px]">
              <label className="text-[14px] font-medium text-text-secondary">
                {t("users.role")} <span className="text-danger">*</span>
              </label>
              <SelectRoot
                value={form.roleId}
                onValueChange={(v) => setForm((f) => ({ ...f, roleId: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("users.selectRolePlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </SelectRoot>
              {errors.roleId && (
                <p className="text-[12px] text-danger">{errors.roleId}</p>
              )}
            </div>

            <div className="flex flex-col gap-[6px]">
              <label className="text-[14px] font-medium text-text-secondary">
                {t("users.entityDepartment")}
              </label>
              <SelectRoot
                value={form.entityId || "NONE"}
                onValueChange={(v) =>
                  setForm((f) => ({ ...f, entityId: v === "NONE" ? "" : v }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("users.selectEntityPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">{t("common.none")}</SelectItem>
                  {entities.map((entity) => (
                    <SelectItem key={entity.id} value={entity.id}>
                      {entity.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </SelectRoot>
            </div>
          </div>

          {/* Status switch */}
          <div className="flex items-center justify-between rounded-control border border-border-subtle px-4 py-3">
            <div className="flex flex-col gap-0.5">
              <span className="text-[14px] font-medium text-text-secondary">
                {t("users.accountStatus")}
              </span>
              <span className="text-caption text-text-muted">
                {t("users.accountStatusDesc")}
              </span>
            </div>
            <Switch
              checked={form.isActive}
              onCheckedChange={(v) => setForm((f) => ({ ...f, isActive: v }))}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 flex items-center justify-end gap-3 border-t border-border-subtle pt-5">
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate("/users")}
            disabled={isSubmitting}
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            leadingIcon={<UserPlus className="h-4 w-4" />}
            isLoading={isSubmitting}
          >
            {t("users.createUser")}
          </Button>
        </div>
      </form>
    </>
  );
}
